import React, { useContext, useEffect, useState } from 'react';
import { Button, Label, Modal, Page } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import {
  getTranslatedText,
  SaveCard,
  IndicatorSeparator,
  DropdownIndicator,
  useActivityTracker,
  EventEmitter
} from '@nile/components';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  fetchCollections,
  getParentHierarchy,
  convertToHierarchy
} from '@nile/rmm/src/modules/knowledge-base/helper';
import CreatableSelect from '../../components/CreatableSelect';
import { isEmpty, last, noop, set } from 'lodash';
import { CREATE_ARTICLE_ID } from '@nile/rmm/src/modules/knowledge-base/helper/constants';
import { useMutation } from '@apollo/client';
import { CREATE_KB_COLLECTION } from '../../graphql';
import { AccountClient } from '@nile/graphql';
import { KB_CONSTANTS } from '../../constants/kb-constants';
import { useKbContext } from '../../contexts/KbContext';
import { transformDataToTree } from '@nile/rmm/src/modules/knowledge-base/components/helper';

const defaultValue = {
  label: null,
  value: null,
  id: null,
  itemId: null,
  parent: null
};
function MoveArticle({
  activator,
  article,
  setArticle,
  onArticleSave,
  onMove
}) {
  const { parent } = article || {};
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creatingCollection, setCreatingCollection] = useState(false);
  const intl = useIntl();

  const [parentCollection, setParentCollection] = useState([
    defaultValue,
    defaultValue
  ]);

  //activator is the button that opens the Modal
  const activatorButton = activator ? (
    <div onClick={() => setOpen((prevState) => !prevState)}>{activator}</div>
  ) : (
    <div
      className="flex actr"
      onClick={() => setOpen((prevState) => !prevState)}
    >
      <SVGIcon name="edit" />
    </div>
  );

  const moveArticle = async () => {
    const filteredParentCollection = parentCollection?.filter(
      (el) => el?.itemId || el?.value
    );
    setLoading(true);
    if (onMove) {
      onMove({
        parent: { itemId: last(filteredParentCollection)?.itemId }
      });
      setLoading(false);
      setOpen(false);
      return;
    }
    if (article?.itemId && article?.itemId !== CREATE_ARTICLE_ID) {
      useActivityTracker({
        event: 'KB_ARTICLE_FOLDER_MOVE'
      });
      await onArticleSave({
        articleProps: {
          parent: { itemId: last(filteredParentCollection)?.itemId }
        }
      });
      setLoading(false);
      setOpen(false);
    } else {
      const formattedParentCollection = convertToHierarchy(
        filteredParentCollection
      );
      await setArticle((prev) => ({
        ...prev,
        parent: formattedParentCollection
      }));
      setLoading(false);
      setOpen(false);
    }
  };

  const handleChange = (newValue, index) => {
    let updatedParentCollection;
    if (index === 0) {
      updatedParentCollection = parentCollection.map((_, i) =>
        i === index ? newValue?.[0] : defaultValue
      );
    } else {
      updatedParentCollection = set(
        parentCollection,
        `${[index]}`,
        newValue?.[0]
      );
    }
    setParentCollection(updatedParentCollection);
  };

  useEffect(() => {
    if (open) {
      const parentColl = getParentHierarchy(parent);
      if (!isEmpty(parentColl)) {
        const clonedParentColl = [...parentCollection];
        parentColl.forEach((el, idx) => {
          if (idx < clonedParentColl?.length) {
            clonedParentColl[idx] = {
              ...clonedParentColl[idx],
              ...el,
              label: el?.name,
              value: el?.itemId
            };
          }
        });
        setParentCollection(clonedParentColl);
      }
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={() => setOpen((prevState) => !prevState)}
      activator={activatorButton}
      showCloseButton={false}
      medium
    >
      {open ? (
        <Page
          title={getTranslatedText(intl, 'common.app.kb.article.move.title')}
          primaryAction={
            <div className="flex-center">
              <SaveCard
                isNewLayout
                hideSaveIcon
                loading={loading}
                disableSaveButton={creatingCollection}
                handleSave={moveArticle}
                actionName={getTranslatedText(
                  intl,
                  'mail.client.conversation.button'
                )}
                actions={{ back: () => setOpen(false) }}
              />
            </div>
          }
          variant="secondary"
          divider
          contentHeight="100px"
        >
          <div className="flex fcol g20 pt20 pb20 pl40 pr40">
            {parentCollection?.map((el, index) => (
              <CollectionSelector
                intl={intl}
                updateParent={(val) => handleChange(val, index)}
                parentFolder={index === 0 ? null : parentCollection[index - 1]}
                currentFolder={parentCollection[index]}
                isCollection={index === 0}
                setCreatingCollection={setCreatingCollection}
                creatingCollection={creatingCollection}
              />
            ))}
          </div>
        </Page>
      ) : (
        <></>
      )}
    </Modal>
  );
}

const CollectionSelector = ({
  intl,
  currentFolder,
  parentFolder,
  updateParent,
  isCollection,
  setCreatingCollection = noop,
  creatingCollection
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const { setTreeItems } = useKbContext();

  const [createCollection] = useMutation(CREATE_KB_COLLECTION, {
    client: AccountClient,
    onCompleted: (data) => {
      if (data?.createKbCollection) {
        updateParent([
          {
            label: data?.createKbCollection?.name,
            value: data?.createKbCollection?.itemId,
            id: data?.createKbCollection?.itemId,
            ...data?.createKbCollection
          }
        ]);
        //Updating Collection Tree with new collection
        if (setTreeItems) {
          setTreeItems((prev) =>
            transformDataToTree([data.createKbCollection], prev)
          );
          EventEmitter.dispatch(KB_CONSTANTS.KB_LIST_TABLE_REFETCH_EVENT);
        }
      }
    },
    onError: () => {
      console.error('Error creating collection');
    }
  });

  const fetchCollectionItems = async (parent) => {
    setLoading(true);
    let condition = [
      {
        attribute: 'itemType',
        operator: 'is',
        value: KB_CONSTANTS.KB_COLLECTION
      }
    ];
    if (isCollection) {
      condition.push({
        attribute: 'parent',
        operator: 'isEmpty'
      });
    }
    if (parent?.itemId) {
      condition.push({
        attribute: 'parent.itemId',
        operator: 'is',
        value: `${parent?.itemId}`
      });
    }
    const res = await fetchCollections({
      listInfo: {
        sort: [
          {
            attribute: 'name',
            order: 'ASC'
          }
        ],
        condition: {
          joinOperator: 'AND',
          operands: condition
        }
      }
    });
    const collections =
      res?.collections?.map((el) => ({
        ...el,
        id: el?.itemId,
        label: el?.name,
        value: el?.itemId
      })) || [];
    setOptions(collections);
    setLoading(false);
  };

  const handleOptionCreation = async (option) => {
    const input = {
      name: option?.value?.trim(),
      parent: null
    };

    // Add parent only if it's a sub collection
    if (!isCollection) {
      input.parent = {
        itemId: parentFolder?.itemId
      };
    }

    setCreatingCollection(true);
    await createCollection({
      variables: {
        input
      }
    });
    setCreatingCollection(false);
  };

  useEffect(() => {
    if (currentFolder) {
      setSelectedOptions([currentFolder]);
    } else {
      setSelectedOptions([]);
    }
  }, [currentFolder, parentFolder]);

  return (
    <div className="flex fcol g5">
      <Label>
        <FormattedMessage
          id={
            isCollection
              ? 'common.app.kb.collection.label'
              : 'common.app.kb.sub.collection.label'
          }
        />
      </Label>
      <div className="flex g10 actr pl3">
        <SVGIcon name="FolderPlain" />
        <div className="full-width">
          <CreatableSelect
            selectedOptions={selectedOptions}
            isClearable
            isEdit={false}
            isDelete={false}
            loading={loading || creatingCollection}
            disabled={!isCollection && isEmpty(parentFolder?.itemId)}
            updateSelectedOptions={(val) => {
              setSelectedOptions(val);
              updateParent(val);
            }}
            createLabel={
              <Button icon={<SVGIcon name="Add" />} plain>
                {getTranslatedText(intl, 'common.app.kb.kbitem.create.new', {
                  name: isCollection
                    ? getTranslatedText(intl, 'common.app.kb.collection.label')
                    : getTranslatedText(
                        intl,
                        'common.app.kb.sub.collection.label'
                      )
                })}
              </Button>
            }
            placeholder={getTranslatedText(
              intl,
              'admin.employee.empInfo.desig.pl'
            )}
            name="designation"
            options={options}
            components={{
              IndicatorSeparator,
              DropdownIndicator
            }}
            onOptionCreate={handleOptionCreation}
            onMenuOpen={() => fetchCollectionItems(parentFolder)}
            menuPlacement="auto"
            closeMenuOnScroll={(e) => {
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default MoveArticle;
export { CollectionSelector };
